import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { FieldModel } from '../../../../models/blueprint/blueprint-field.model';
import { IStyledDropdownOption } from '../../../../models/blueprint/blueprint-field-dropdown-single.model';
import { BlueprintDataHelper } from '../../../../services/helpers/blueprint-data.helper';
import { Select } from 'primeng/select';

const URL_PATTERN = /^(https?:\/\/|ftp:\/\/)([\w-]+\.)+[a-z]{2,}(\/.*)?$/i;

@Component({
  selector: 'app-dynamic-field-input',
  templateUrl: './dynamic-field-input.component.html',
  styleUrls: ['./dynamic-field-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class DynamicFieldInputComponent implements OnChanges {
  private blueprintDataHelper = inject(BlueprintDataHelper);
  @ViewChild('userSelect') userSelect?: Select;
  @Input() field!: FieldModel;
  @Input() value: any;
  @Input() index: number = -1;
  @Input() projectId = '';
  @Input() workspaceId = '';
  @Input() nodeInstanceId = '';
  @Input() nodeKey = '';
  @Input() buildDropdownOptions: (options: any[]) => { label: string; value: any }[] = () => [];
  @Input() buildUserDropdownOptions: (users: any[]) => { label: string; value: string; disabled?: boolean }[] = () => [];

  @Output() valueChange = new EventEmitter<any>();
  @Output() assigneeChange = new EventEmitter<{ index: number; userId: string; fieldId: string; fieldName: string }>();
  @Output() dateChange = new EventEmitter<{ index: number; value: Date | string | null; fieldId: string }>();
  @Output() dateClear = new EventEmitter<{ index: number; fieldId: string }>();
  @Output() dateSet = new EventEmitter<void>();

  tempDate: Date | null = null;
  private reopenUserSelectAfterLoading = false;

  ngOnChanges(changes: SimpleChanges): void {
    const wasLoadingUserOptions = changes['field']?.previousValue?.value?.isLoadingOptions === true;
    const hasLoadedUserOptions = wasLoadingUserOptions && this.field?.value?.isLoadingOptions !== true;

    if (changes['value'] && this.isDateField) {
      if (this.value && typeof this.value === 'string') {
        this.value = new Date(this.value);
      } else if (!this.value && this.field?.value?.value) {
        // Load default value from blueprint if current value is not set
        this.value = new Date(this.field.value.value);
      }
      this.tempDate = this.value ? new Date(this.value) : null;
    }

    if (changes['value'] && this.fieldType === 'dropdownsingle' && !this.value) {
      if (this.isStandardDropdown) {
        // Standard dropdown: only auto-select when hasDefault is explicitly configured
        if (this.field?.value?.hasDefault && this.field?.value?.value) {
          this.value = this.field.value.value;
        } else {
          // No default: show the "Select" placeholder option
          this.value = '';
        }
      } else {
        // Styled dropdown (Status / Priority / Styled): respect hasDefault flag
        const hasDefault = this.field?.value?.hasDefault ?? this.field?.value?.HasDefault;
        if (hasDefault === false) {
          this.value = '';
        } else {
          const first = this.styledDropdownOptions[0];
          if (first) {
            this.value = first.value;
          }
        }
      }
    }

    if (hasLoadedUserOptions && this.reopenUserSelectAfterLoading && this.fieldType === 'user') {
      setTimeout(() => {
        if (!this.isReadOnly) {
          this.userSelect?.show();
        }
        this.reopenUserSelectAfterLoading = false;
      });
    }
  }

  // Computed common properties for cleaner template
  get fieldName(): string {
    return this.field?.label || this.field?.name || '';
  }

  get fieldTooltip(): string {
    return this.field?.tooltip || this.field?.name || '';
  }
  get isRequired(): boolean {
    return this.field?.value?.isRequired ?? false;
  }
  get isReadOnly(): boolean {
    return this.field?.value?.isReadOnly ?? false;
  }
  get maxLength(): number {
    return this.field?.value?.maxLength;
  }
  get min(): number {
    return this.field?.value?.min;
  }
  get max(): number {
    return this.field?.value?.max;
  }
  get options(): any[] {
    return this.field?.value?.options || [];
  }

  get isUserOptionsLoading(): boolean {
    return this.field?.value?.isLoadingOptions === true;
  }

  get userDropdownOptions(): { label: string; value: string; disabled?: boolean }[] {
    if (this.isUserOptionsLoading) {
      return [{ label: 'Loading...', value: '__loading__', disabled: true }];
    }
    return this.buildUserDropdownOptions(this.options);
  }

  get isStandardDropdown(): boolean {
    return !this.blueprintDataHelper.isStyledFieldCategory(this.field?.value?.fieldCategory, (this.field?.value as any)?.fieldType);
  }

  get styledDropdownOptions(): IStyledDropdownOption[] {
    const options = this.blueprintDataHelper.mapStatusOptionsToStyled(this.field?.value?.statusOptions || []);
    const hasDefault = this.field?.value?.hasDefault ?? this.field?.value?.HasDefault;
    if (hasDefault === false) {
      return [{ label: 'Select', value: '', fillColor: '', textColor: '', borderColor: '', borderThickness: 0 }, ...options];
    }
    return options;
  }

  get standardDropdownOptions(): { label: string; value: any }[] {
    return [{ label: 'Select', value: '' }, ...this.buildDropdownOptions(this.options)];
  }
  get maskExpression(): string {
    return this.field?.value?.maskExpression || '';
  }
  get fieldId(): string {
    return this.field?.id || '';
  }

  get isMasked(): boolean {
    return this.field?.value?.isMasked ?? false;
  }

  get defaultText(): string {
    const val = this.field?.value?.value;
    return typeof val === 'string' ? val : '';
  }

  get fieldType(): string {
    const type = this.field?.fieldType?.toLowerCase() || '';
    return type.startsWith('blueprintfield') ? type.replace('blueprintfield', '') : type;
  }

  // Check if this is a date/datetime field type
  get isDateField(): boolean {
    return this.fieldType === 'datetime' || this.fieldType === 'date';
  }

  // Check if this is a dropdown field type
  get isDropdownField(): boolean {
    return this.fieldType === 'dropdownsingle' || this.fieldType === 'dropdownmultiple';
  }


  // Event handlers for two-way binding with custom events
  onValueChange(newValue: any): void {
    this.valueChange.emit(newValue);
  }

  onAssigneeChange(userId: string): void {
    this.assigneeChange.emit({ index: this.index, userId, fieldId: this.fieldId, fieldName: this.fieldName });
  }

  onUserSelectShow(): void {
    if (this.isUserOptionsLoading) {
      this.reopenUserSelectAfterLoading = true;
    }
  }

  onUserSelectHide(): void {
    if (!this.isUserOptionsLoading) {
      this.reopenUserSelectAfterLoading = false;
    }
  }

  onDateClear(datePicker?: any): void {
    this.tempDate = null;
    this.value = null;
    if (datePicker) {
      datePicker.overlayVisible = false;
    }
    this.dateClear.emit({ index: this.index, fieldId: this.fieldId });
  }

  onDateChange(event: any): void {
    this.tempDate = event;
    // Don't emit or close here, wait for Set/Clear
  }

  onDateSet(datePicker?: any): void {
    this.value = this.tempDate;
    if (datePicker) {
      datePicker.overlayVisible = false;
    }
    this.dateChange.emit({ index: this.index, value: this.value, fieldId: this.fieldId });
    this.dateSet.emit();
  }

  // Helper to emit blur event for text inputs
  onInputBlur(inputValue: any): void {
    this.valueChange.emit(inputValue);
  }

  // Helper to emit enter key event for text inputs
  onEnterSubmit(event: KeyboardEvent, inputValue: any): void {
    event.preventDefault();
    event.stopPropagation();
    this.valueChange.emit(inputValue);
  }

  // URL validation
  get isValidUrl(): boolean {
    return !!this.value && URL_PATTERN.test(this.value);
  }

  urlInvalid = false;

  onUrlBlur(inputValue: string): void {
    if (!inputValue || inputValue.trim() === '') {
      this.urlInvalid = false;
      this.valueChange.emit('');
      return;
    }
    if (URL_PATTERN.test(inputValue.trim())) {
      this.urlInvalid = false;
      this.valueChange.emit(inputValue.trim());
    } else {
      this.urlInvalid = true;
    }
  }
}
